import type { CatalogListItem } from "@/lib/catalog-item";
import { ItemActionsMenu, type ItemActionsProps } from "./item-actions-menu";
import { ItemSelectCheckbox, type ItemSelectionProps } from "./item-select-checkbox";

// The one control in a card's or a row's top-right corner: the batch tick, or
// the 3-dot actions menu, or neither.
//
// **One slot, two occupants, never both.** The donor draws
// `{selectable ? <checkbox/> : showItemActions && <menu/>}` in a single corner
// (`item-card.tsx:175-180`, `item-data-row.tsx:162-167`) — they are alternatives
// sharing a position, not neighbours.
//
// So the ternary here is the donor's, not a defensive check: the tick wins the
// corner and the menu takes it only when there is no tick. `item-catalog-page`
// already builds the actions bundle only where the selection bundle is absent,
// which means the `else` arm is the only one that can be reached today — but a
// third view, or a test, handing over both would otherwise stack two controls
// in one 8px corner, and the failure would be visual rather than a type error
// (#457, code review). Written this way there is nothing to enforce.
//
// Here rather than twice over because both views had the same two lines and the
// same comment explaining `ownItem`, which is one place for a third view to
// forget (#457, code review).

export function ItemCornerControls({
  /**
   * The item the corner's control acts on — the card's or row's **own** item,
   * never the variant currently being previewed.
   *
   * The donor is explicit about it: "the admin actions menu ... always operates
   * on this card's item rather than the variant currently being previewed"
   * (`item-card.tsx:52-56`). A batch operation follows the same rule, for the
   * same reason — the row is what got ticked.
   */
  ownItem,
  selection,
  actions,
}: { ownItem: CatalogListItem } & ItemSelectionProps & ItemActionsProps) {
  return selection ? (
    <ItemSelectCheckbox item={ownItem} {...selection} />
  ) : (
    actions && <ItemActionsMenu item={ownItem} {...actions} />
  );
}
